import React, { useState } from 'react';
import { Task } from '../types';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface TaskItemProps {
  task: Task;
  index: number;
  onToggleComplete: (taskId: string, completed: boolean) => void;
}

const TaskItem: React.FC<TaskItemProps> = ({ task, index, onToggleComplete }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Safely handle checkbox change
  const handleCheckboxChange = (checked: boolean | "indeterminate") => {
    try {
      onToggleComplete(task.id, checked === true);
    } catch (error) {
      console.error('Error toggling task completion:', error);
    }
  };
  
  return (
    <div className="border-b border-zinc-200 dark:border-zinc-700 last:border-b-0">
      <div 
        className={`
          flex items-start sm:items-center px-4 sm:px-6 py-4 cursor-pointer gap-3 sm:gap-4
          ${task.isCompleted ? 'bg-zinc-50/80 dark:bg-zinc-800/50' : 'bg-card dark:bg-zinc-800 hover:bg-zinc-50/50 dark:hover:bg-zinc-700/50'}
          transition-all duration-200
        `}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Checkbox with improved mobile touch target */}
        <div className="min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0">
          <div 
            className="relative" 
            onClick={(e) => {
              e.stopPropagation();
              handleCheckboxChange(!task.isCompleted);
            }}
          >
            <Checkbox
              checked={task.isCompleted}
              onCheckedChange={handleCheckboxChange}
              className={`h-5 w-5 rounded-md border-2 transition-colors duration-200 ${
                task.isCompleted 
                  ? 'bg-primary border-primary text-primary-foreground dark:bg-primary dark:border-primary' 
                  : 'border-zinc-300 dark:border-zinc-600 hover:border-primary/70 dark:hover:border-primary/70 bg-card dark:bg-zinc-800'
              }`}
              aria-label={`Mark task ${task.title || 'Untitled'} as ${task.isCompleted ? 'incomplete' : 'complete'}`}
            />
            {/* Invisible larger hit area */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[44px] h-[44px] cursor-pointer"></div>
          </div>
        </div>
        
        {/* Task number - responsive sizing */}
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 font-medium text-sm flex-shrink-0">
          {index}
        </div>
        
        {/* Task Content - improved mobile layout */}
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-start sm:items-center justify-between gap-2">
            <h4 className={`font-medium text-base sm:text-base transition-all duration-200 break-words leading-tight sm:leading-normal ${
              task.isCompleted 
                ? 'text-zinc-500 dark:text-zinc-400 line-through' 
                : 'text-zinc-800 dark:text-zinc-200'
            }`}>
              {task.title || 'Untitled Task'}
            </h4>
            
            {task.details && task.details.trim() !== '' && (
              <div className="text-zinc-400 dark:text-zinc-500 flex-shrink-0 mt-1 sm:mt-0">
                {expanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Expandable details section */}
      {task.details && task.details.trim() !== '' && (
        <div 
          className={`
            transition-all duration-300 overflow-hidden bg-zinc-50/50 dark:bg-zinc-800/30
            ${expanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}
          `}
        >
          <div className="px-4 sm:px-6 pb-4 ml-[60px] sm:ml-[76px] pr-4 sm:pr-6 text-sm leading-relaxed max-h-[800px] overflow-y-auto">
            <div className={`
              border-l-2 border-zinc-200 dark:border-zinc-700 pl-4 py-2 space-y-3
              ${task.isCompleted ? 'text-zinc-500 dark:text-zinc-400' : 'text-zinc-600 dark:text-zinc-300'}
            `}>
              {task.details.split('\n').map((paragraph, idx) => (
                <p key={idx} className="leading-[24px] break-words">{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskItem; 